import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Box,
  styled,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import OilBarrelIcon from "@mui/icons-material/OilBarrel";
import DesignServicesIcon from "@mui/icons-material/DesignServices";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import VerticalSplitIcon from "@mui/icons-material/VerticalSplit";
import CategoryIcon from "@mui/icons-material/Category";
import SellIcon from "@mui/icons-material/Sell";
import PermContactCalendarIcon from "@mui/icons-material/PermContactCalendar";
import CalculateIcon from "@mui/icons-material/Calculate";
import WorkspacesIcon from "@mui/icons-material/Workspaces";
import GradientIcon from "@mui/icons-material/Gradient";
import CompressIcon from "@mui/icons-material/Compress";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ArticleIcon from "@mui/icons-material/Article";
import ViewListIcon from "@mui/icons-material/ViewList";
import ScienceIcon from "@mui/icons-material/Science";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import AuthContext from "../context/AuthContext";

interface Props {
  onCloseMenu: () => void;
  isDrawerOpen: boolean;
}

const StyledList = styled(List)({
  padding: "8px 0",
  color: "white",
});

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  borderRadius: "8px",
  margin: "4px 8px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.1)",
  },
  "& .MuiListItemIcon-root": {
    minWidth: "auto",
    marginRight: "12px",
  },
  "& .MuiListItemText-root": {
    margin: 0,
  },
}));

const StyledListItemIcon = styled(ListItemIcon)({
  backgroundColor: "rgb(251,171,53, 0.9)",
  padding: "6px",
  color: "white",
  borderRadius: "8px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "32px",
  height: "32px",
  fontSize: "18px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  minWidth: "32px",
  "& svg": {
    fontSize: "18px",
  },
});

const SubMenuListItemButton = styled(ListItemButton)({
  borderRadius: "8px",
  margin: "4px 8px",
  paddingLeft: "32px",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.1)",
  },
  "& .MuiListItemIcon-root": {
    minWidth: "auto",
    marginRight: "8px",
  },
  "& .MuiListItemText-root": {
    margin: 0,
    "& .MuiTypography-root": {
      fontSize: "13px",
      fontWeight: "600",
    },
  },
});

const SubMenuIcon = styled(ListItemIcon)({
  backgroundColor: "rgb(251,171,53, 0.9)",
  padding: "4px",
  color: "white",
  borderRadius: "6px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: "24px",
  height: "24px",
  minWidth: "24px",
  "& svg": {
    fontSize: "14px",
  },
});

export const CustomNavigationList = ({ onCloseMenu, isDrawerOpen }: Props) => {
  const { idRole } = useContext(AuthContext);
  const [openPropOption, setOpenPropOption] = useState(false);
  const [openTallyOption, setOpenTallyOption] = useState(false);
  const [openSimOption, setOpenSimOption] = useState(false);
  const [openPullingOption, setOpenPullingOption] = useState(false);
  const [documentOption, setDocumentOption] = useState(idRole === 3);
  const [openMoreOption, setOpenMoreOption] = useState(false);
  const navigate = useNavigate();

  const handleClick = (action: () => void, resetAll = true) => {
    if (resetAll) {
      setOpenPropOption(false);
      setOpenTallyOption(false);
      setOpenSimOption(false);
      setOpenPullingOption(false);
      setDocumentOption(false);
      setOpenMoreOption(false);
    }
    action();
  };

  const navItems = [
    {
      id: 0,
      onClick: () => navigate("/home"),
      icon: <HomeIcon />,
      name: "Home",
      roles: [0, 1, 2],
    },
    {
      id: 2,
      onClick: () =>
        handleClick(() => setOpenPropOption(!openPropOption), false),
      icon: <PrecisionManufacturingOutlinedIcon />,
      name: "Technical Design",
      roles: [0, 1, 2],
      openCloseOption: openPropOption,
      children: [
        {
          id: 0,
          onClick: () => navigate("/tech/0"),
          icon: <DesignServicesIcon />,
          name: "Create",
          roles: [0, 1],
        },
        {
          id: 1,
          onClick: () => navigate("/tech/all"),
          icon: <FormatListNumberedIcon />,
          name: "See All",
        },
      ],
    },
    {
      id: 3,
      onClick: () =>
        handleClick(() => setOpenTallyOption(!openTallyOption), false),
      icon: <VerticalSplitIcon />,
      name: "Tally Design",
      roles: [0, 1, 2],
      openCloseOption: openTallyOption,
      children: [
        {
          id: 0,
          onClick: () => navigate("/tally/0"),
          icon: <DesignServicesIcon />,
          name: "Create",
          roles: [0, 1],
        },
        {
          id: 1,
          onClick: () => navigate("/tally/all"),
          icon: <FormatListNumberedIcon />,
          name: "See All",
        },
      ],
    },
    {
      id: 4,
      onClick: () =>
        handleClick(() => setOpenPullingOption(!openPullingOption), false),
      icon: <AutoFixHighIcon />,
      name: "Pulling Design",
      roles: [0],
      openCloseOption: openPullingOption,
      children: [
        {
          id: 0,
          onClick: () => navigate("/pulling/0"),
          icon: <DesignServicesIcon />,
          name: "Create",
        },
        {
          id: 1,
          onClick: () => navigate("/pulling/all"),
          icon: <FormatListNumberedIcon />,
          name: "See All",
        },
      ],
    },
    {
      id: 9,
      onClick: () => handleClick(() => setOpenSimOption(!openSimOption), false),
      icon: <CalculateIcon />,
      name: "Simulators",
      roles: [0, 1],
      openCloseOption: openSimOption,
      children: [
        {
          id: 0,
          onClick: () => navigate("/sim/sand"),
          icon: <GradientIcon />,
          name: "Sand",
        },
        {
          id: 1,
          onClick: () => navigate("/sim/gas"),
          icon: <WorkspacesIcon />,
          name: "Gas",
        },
        {
          id: 2,
          onClick: () => navigate("/sim/press"),
          icon: <CompressIcon />,
          name: "Pressure",
        },
      ],
    },
    {
      id: 10,
      onClick: () =>
        handleClick(() => setDocumentOption(!documentOption), false),
      icon: <DocumentScannerIcon />,
      name: "Documents",
      openCloseOption: documentOption,
      roles: [0, 1, 2, 3],
      children: [
        {
          id: 100,
          onClick: () => navigate("/proposal"),
          icon: <ArticleIcon />,
          name: "Proposals",
          roles: [0, 1, 2, 3],
        },
        {
          id: 101,
          onClick: () => navigate("/pulling-report"),
          icon: <ViewListIcon />,
          name: "Pulling Reports",
          roles: [0, 1, 2, 3],
        },
        {
          id: 102,
          onClick: () => navigate("/chemical-tracker"),
          icon: <ScienceIcon />,
          name: "Chemical Trackers",
          roles: [0, 1, 2, 3],
        },
        {
          id: 13,
          onClick: () => navigate("/tally-doc"),
          icon: <VerticalSplitIcon />,
          name: "Tally Designs",
          roles: [0, 1, 2, 3],
        },
        {
          id: 103,
          onClick: () => navigate("/additional-documents"),
          icon: <FolderCopyIcon />,
          name: "Additional Documents",
          roles: [0, 1, 2, 3],
        },
        {
          id: 104,
          onClick: () => navigate("/tools-run"),
          icon: <Inventory2OutlinedIcon />,
          name: "Tools Tracker",
          roles: [0, 1, 2, 3],
        },
        {
          id: 105,
          onClick: () => navigate("/coi"),
          icon: <WarehouseOutlinedIcon />,
          name: "COI",
          roles: [0, 1, 2, 3],
        },
        {
          id: 106,
          onClick: () => navigate("/reports"),
          icon: <AssessmentOutlinedIcon />,
          name: "Reports",
          roles: [0, 1, 2, 3],
        },
      ],
    },
    {
      id: 11,
      onClick: () =>
        handleClick(() => setOpenMoreOption(!openMoreOption), false),
      icon: <MoreHorizIcon />,
      name: "More",
      openCloseOption: openMoreOption,
      roles: [0, 1, 2],
      children: [
        {
          id: 5,
          onClick: () => navigate("/well"),
          icon: <OilBarrelIcon />,
          name: "Wells",
          roles: [0, 1, 2],
        },
        {
          id: 6,
          onClick: () => navigate("/client"),
          icon: <PermContactCalendarIcon />,
          name: "Clients",
          roles: [0, 1, 2],
        },
        {
          id: 7,
          onClick: () => navigate("/sales/all"),
          icon: <SellIcon />,
          name: "Sales",
          roles: [0, 1, 2],
        },
        {
          id: 8,
          onClick: () => navigate("/products"),
          icon: <CategoryIcon />,
          name: "Products",
          roles: [0, 1, 2],
        },
      ],
    },
  ];

  return (
    <StyledList>
      {navItems.map((item, index) => (
        <React.Fragment key={index}>
          {(idRole === 0 || idRole) && item.roles.includes(idRole) ? (
            <>
              <ListItem disablePadding>
                <StyledListItemButton
                  onClick={() => {
                    item.onClick();
                    !item.children && onCloseMenu();
                  }}
                  sx={{
                    justifyContent: isDrawerOpen ? "flex-start" : "center",
                    px: isDrawerOpen ? 2 : 1,
                  }}
                >
                  <StyledListItemIcon>{item.icon}</StyledListItemIcon>
                  {isDrawerOpen && (
                    <>
                      <ListItemText
                        primary={item.name}
                        sx={{
                          color: "white",
                          "& .MuiTypography-root": {
                            fontSize: "14px",
                            fontWeight: "700",
                          },
                        }}
                      />
                      {item.children && (
                        <Box sx={{ ml: 1 }}>
                          {item.openCloseOption ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </Box>
                      )}
                    </>
                  )}
                </StyledListItemButton>
              </ListItem>

              {/* Submenu */}
              {item.children && isDrawerOpen && (
                <Collapse
                  in={item.openCloseOption}
                  timeout="auto"
                  unmountOnExit
                >
                  <List component="div" disablePadding>
                    {item.children
                      .filter(
                        (child) =>
                          !child.roles ||
                          (idRole !== null && child.roles.includes(idRole)),
                      )
                      .map((child) => (
                        <ListItem key={child.id} disablePadding>
                          <SubMenuListItemButton
                            onClick={() => {
                              child.onClick();
                              onCloseMenu();
                            }}
                          >
                            <SubMenuIcon>{child.icon}</SubMenuIcon>
                            <ListItemText
                              primary={child.name}
                              sx={{
                                color: "white",
                                "& .MuiTypography-root": {
                                  fontWeight: "600",
                                },
                              }}
                            />
                          </SubMenuListItemButton>
                        </ListItem>
                      ))}
                  </List>
                </Collapse>
              )}
            </>
          ) : null}
        </React.Fragment>
      ))}
    </StyledList>
  );
};
